import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";

// Icons
import editIcon from "../../assets/icons/edit.svg";
import trashIcon from "../../assets/icons/trash.svg";

// Actions
import {
  deleteInjury,
  updateInjury,
  uploadDescriptions,
  uploadTreatment
} from "../../state/injuries/actions";
import { getCaseDetail } from "../../state/caseDetail/actions";
import { toggleSnack } from "../../state/snackbar/actions";

// Config
import { getRoute, urls } from "../../config/routes";
import { sensitiveSections } from "../../config/values";
import { snackbarTypes } from "../../config/snackbar";
import styles from "../../config/styles";
import isCaseLocked from "../../utils/isCaseLocked";

// Hooks
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import useHideToWitnesses from "../../hooks/useHideToWitnesses";
import useInjuries from "../../hooks/useInjuries";
import useTranslate from "../../hooks/useTranslate";

// Components
import BodyMap from "../BodyMap";
import ConfirmationButton from "../ConfirmationButton";
import ContentCardHeader from "../ContentCardHeader";
import DetailValue from "../DetailValue";
import ImagesBox from "./ImagesBox";
import LoadingSection from "../LoadingSection";
import Text from "../Text";

const columnStyle = {
  display: "flex",
  flexDirection: "column"
};

const useStyles = makeStyles(theme => ({
  data: {
    border: styles.values.border
  },
  title: {
    fontSize: 24
  },
  iconButton: {
    padding: 0,
    height: 40,
    width: 40,
    marginLeft: theme.spacing(1.5)
  },
  content: {
    display: "flex",
    flexDirection: "row",
    padding: theme.spacing(4, 3.5)
  },
  leftColumn: {
    ...columnStyle,
    marginRight: theme.spacing(4),
    width: "100%"
  },
  rightColumn: {
    ...columnStyle,
    width: 256,
    minWidth: 256
  },
  rightTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2)
  }
}));

const InjuryDetailPanel = ({ caseId, injuryId }) => {
  const { data: caseDetail } = useSelector(s => s.caseDetail);
  const caseLocked = isCaseLocked(caseDetail);
  const { trackException } = useAppInsights();
  const classes = useStyles();
  const dispatch = useDispatch();
  const getText = useTranslate();
  const history = useHistory();

  const {
    data,
    imageHeaders,
    isDeletingInjury,
    isUploadingDescription,
    isUploadingTreatment,
    loadingHeaders,
    error,
    initialized
  } = useInjuries(caseId, injuryId);

  const { descriptions, treatments } = data || {};

  useHideToWitnesses(caseDetail);

  // data can still hold a previously loaded, different injury while this
  // one's fetch is in flight - checking its id (not just data/isLoading)
  // avoids rendering that stale injury's content, and initialized (settled
  // one way or another for this exact injuryId) avoids redirecting away on
  // a leftover error from a different injury that failed earlier.
  const isCurrentInjuryLoaded = data && data.id === injuryId;

  useEffect(() => {
    if (!isCurrentInjuryLoaded && initialized && error) {
      history.push(getRoute(urls.caseDetail, { caseId }));
    }
  }, [caseId, error, history, initialized, isCurrentInjuryLoaded]);

  if (!isCurrentInjuryLoaded) {
    return (
      <Paper elevation={0} className={classes.data}>
        <LoadingSection />
      </Paper>
    );
  }

  const getImageRoute = index =>
    getRoute(urls.annotationTool, {
      caseId,
      injuryId,
      index
    });

  const handleDeleteInjury = () => {
    dispatch(
      deleteInjury({
        caseId,
        injuryId,
        onFinish: success => {
          if (success) history.push(getRoute(urls.caseDetail, { caseId }));
          else {
            trackException(
              `Failed to delete injury: ${injuryId} at case: ${caseId}`
            );
            // Refetches in case the rejection happened because the case
            // became locked mid-edit, so the UI picks up the current state.
            dispatch(getCaseDetail({ caseId, isUpdate: true }));
            dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
          }
        }
      })
    );
  };

  const isSensitive = sensitiveSections.includes(data.bodySection);

  const currentDescription = (descriptions[0] && descriptions[0].text) || "";
  const currentTreatment = (treatments[0] && treatments[0].text) || "";

  // Refetches in case the rejection happened because the case became locked
  // mid-edit, so the UI picks up the current state - DetailValue only tracks
  // the failure via telemetry, it doesn't show the user anything on its own.
  const onMutationFail = onFinish => success => {
    if (!success) {
      dispatch(getCaseDetail({ caseId, isUpdate: true }));
      dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
    }
    onFinish(success);
  };

  const handleUpdate = ({ form, onFinish }) =>
    dispatch(
      updateInjury({ caseId, injuryId, form, onFinish: onMutationFail(onFinish) })
    );

  const handleAddDescription = ({ texts, onFinish }) =>
    dispatch(
      uploadDescriptions({
        caseId,
        injuryId,
        texts,
        onFinish: onMutationFail(onFinish)
      })
    );

  const handleAddTreatment = ({ texts, onFinish }) =>
    dispatch(
      uploadTreatment({
        caseId,
        injuryId,
        texts,
        onFinish: onMutationFail(onFinish)
      })
    );

  const buildUpdateForm = name => value => ({
    ...data,
    description: name === "description" ? value : currentDescription,
    treatment: name === "treatment" ? value : currentTreatment
  });

  return (
    <Paper elevation={0} className={classes.data}>
      <ContentCardHeader>
        <Text type="heading2" className={classes.title}>
          {getText(`common.bodySection.${data.bodySection}`)}{" "}
          {getText(`common.injuryCategory.${data.category}.shortName`)}
        </Text>
        <div>
          <IconButton
            className={classes.iconButton}
            onClick={() =>
              history.push(getRoute(urls.editInjury, { caseId, injuryId }))
            }
          >
            <img src={editIcon} alt="" />
          </IconButton>
          {!caseLocked && (
            <ConfirmationButton
              className={classes.iconButton}
              type="icon"
              onClick={handleDeleteInjury}
              dialog={{
                isLoading: isDeletingInjury,
                texts: [
                  {
                    key: 1,
                    text: getText("injuryDetailPage.deleteButtonDialog.title")
                  },
                  {
                    key: 2,
                    text: getText(
                      "injuryDetailPage.deleteButtonDialog.description"
                    )
                  }
                ]
              }}
            >
              <img src={trashIcon} alt="" />
            </ConfirmationButton>
          )}
        </div>
      </ContentCardHeader>
      <div className={classes.content}>
        <div className={classes.leftColumn}>
          <DetailValue
            addButtonText={getText("caseIncidentsPage.descriptionAddButton")}
            buildUpdateForm={buildUpdateForm("description")}
            caseId={caseId}
            itemId={injuryId}
            canAddMore={!caseLocked}
            content={data.descriptions}
            isDraft={caseDetail.isDraft}
            isUpdating={isUploadingTreatment}
            isUploadingAdd={isUploadingDescription}
            name="description"
            onAdd={handleAddDescription}
            onUpdate={handleUpdate}
            title={getText("common.valueLabels.description")}
          />
          <DetailValue
            addButtonText={getText("editInjuryPage.treatmentInputAddButton")}
            buildUpdateForm={buildUpdateForm("treatment")}
            caseId={caseId}
            itemId={injuryId}
            canAddMore={!caseLocked}
            content={data.treatments}
            isDraft={caseDetail.isDraft}
            isUpdating={isUploadingTreatment}
            isUploadingAdd={isUploadingTreatment}
            name="treatment"
            onAdd={handleAddTreatment}
            onUpdate={handleUpdate}
            title={getText("common.valueLabels.treatment")}
          />
        </div>
        <div className={classes.rightColumn}>
          {data.images && (
            <ImagesBox
              imageHeaders={imageHeaders}
              imageRequest={{ caseId, injuryId }}
              images={data.images}
              isSensitive={isSensitive}
              loadingHeaders={loadingHeaders}
              to={getImageRoute}
            />
          )}
          {data.bodySection && (
            <>
              <Text
                className={classes.rightTitle}
                text="common.valueLabels.bodyMap"
                type="body2"
              />
              <BodyMap partId={data.bodySection} />
            </>
          )}
        </div>
      </div>
    </Paper>
  );
};

InjuryDetailPanel.propTypes = {
  caseId: PropTypes.string.isRequired,
  injuryId: PropTypes.string.isRequired
};

export default InjuryDetailPanel;
